// Inquiries — pre-booking enquiry triage. Customer ↔ vendor threads;
// admin watches + can intervene. Reads /admin/inquiries.

import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useQuery,
} from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import {
  PageHeader,
  ToolChip,
  ToolSearch,
  Toolbar,
} from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import {
  fetchAdminInquiries,
  type AdminInquiriesListParams,
  type AdminInquiryRow,
  type InquiryStatus,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

type FilterKey = NonNullable<AdminInquiriesListParams['status']>;

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'replied', label: 'Replied' },
  { key: 'closed', label: 'Closed' },
];

const fmtTime = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const COLUMNS: DataTableColumn<AdminInquiryRow>[] = [
  {
    key: 'subject',
    h: 'Subject',
    cell: (r) => (
      <div className="max-w-[360px]">
        <div className="truncate font-medium">{r.subject}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.car.title ?? r.car.slug ?? `Car #${r.car.id}`}
        </div>
      </div>
    ),
  },
  {
    key: 'customer',
    h: 'Customer',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.customer.name}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.customer.email}
        </div>
      </div>
    ),
  },
  {
    key: 'vendor',
    h: 'Vendor',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.vendor.name}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.vendor.email}
        </div>
      </div>
    ),
  },
  {
    key: 'messageCount',
    h: 'Messages',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[12.5px]">{r.messageCount}</span>
    ),
  },
  { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
  {
    key: 'updatedAt',
    h: 'Last activity',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[11.5px] text-[color:var(--bo-mute)]">
        {fmtTime(r.updatedAt)}
      </span>
    ),
  },
];

const STATUS_KEYS: InquiryStatus[] = ['open', 'replied', 'closed'];

export function Inquiries() {
  const [filter, setFilter] = useState<FilterKey>('open');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();
  const navigate = useNavigate();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [filter, q, setPage]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'inquiries', { filter, q, page }],
    queryFn: ({ signal }) =>
      fetchAdminInquiries(
        { status: filter, q: q.trim() || undefined, page, limit },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const counts = data?.statusCounts ?? {};
  const openCount = counts.open ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Trust"
        title="Inquiries"
        sub={`${total.toLocaleString()} total · ${openCount} open.`}
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load inquiries: {(error as Error).message}
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch
          value={q}
          onChange={setQ}
          placeholder="Search subject, email, vendor…"
        />
        {FILTERS.map((f) => (
          <ToolChip
            key={f.key}
            active={filter === f.key}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            {f.key !== 'all' && STATUS_KEYS.includes(f.key as InquiryStatus) ? (
              <span className="ms-1 tabular-nums text-[10.5px] text-[color:var(--bo-mute)]">
                {counts[f.key] ?? 0}
              </span>
            ) : null}
          </ToolChip>
        ))}
      </Toolbar>
      <DataTable
        columns={COLUMNS}
        rows={rows}
        emptyLabel={isLoading ? 'Loading…' : 'No matching inquiries.'}
        onRowClick={(r) =>
          void navigate({ to: `/inquiries/${r.id}` as never })
        }
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
      />
    </div>
  );
}
